import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { useAppStore } from '../../store/useAppStore';
import { useSettingsStore } from '../../store/useSettingsStore';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import Modal from '../ui/Modal';

function SettingsOverlay({ open, onClose }) {
  const { t } = useTranslation();
  const language = useSettingsStore((state) => state.language);
  const fontSize = useSettingsStore((state) => state.fontSize);
  const setLanguage = useSettingsStore((state) => state.setLanguage);
  const setFontSize = useSettingsStore((state) => state.setFontSize);
  const bakerPin = useSettingsStore((state) => state.bakerPin);
  const setBakerPin = useSettingsStore((state) => state.setBakerPin);
  const pinValidated = useSettingsStore((state) => state.pinValidated);
  const setPinValidated = useSettingsStore((state) => state.setPinValidated);
  const includePinInShare = useSettingsStore((state) => state.includePinInShare);
  const setIncludePinInShare = useSettingsStore((state) => state.setIncludePinInShare);
  const pinConfigured = useAppStore((state) => state.pinConfigured);
  const checkAuthStatus = useAppStore((state) => state.checkAuthStatus);
  const sessionConfigured = useAppStore((state) => state.sessionConfigured);
  const sessionId = useSettingsStore((state) => state.sessionId);
  const setSessionId = useSettingsStore((state) => state.setSessionId);
  const sessionValidated = useSettingsStore((state) => state.sessionValidated);
  const setSessionValidated = useSettingsStore((state) => state.setSessionValidated);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [qrSvg, setQrSvg] = useState('');
  const [validating, setValidating] = useState(false);
  const [pinInvalid, setPinInvalid] = useState(false);

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    const base = `${window.location.origin}${window.location.pathname}`;
    const params = [];
    if (sessionId) {
      params.push(`sessionId=${encodeURIComponent(sessionId)}`);
    }
    if (bakerPin && includePinInShare) {
      params.push(`bakerPin=${encodeURIComponent(bakerPin)}`);
    }
    return params.length > 0 ? `${base}?${params.join('&')}` : base;
  }, [sessionId, bakerPin, includePinInShare]);

  useEffect(() => {
    if (!open) return undefined;

    setValidating(false);
    setPinInvalid(false);
    if (pinConfigured === null || sessionConfigured === null) {
      checkAuthStatus();
    }
    return undefined;
  }, [open, pinConfigured, sessionConfigured, checkAuthStatus]);

  useEffect(() => {
    if (!open) return undefined;

    setQrSvg('');
    QRCode.toString(shareUrl, {
      type: 'svg',
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((svg) => setQrSvg(svg))
      .catch((error) => console.error('QR generation error', error));
    return undefined;
  }, [open, shareUrl]);

  const handleValidatePin = async () => {
    if (!bakerPin || validating) return;
    setValidating(true);
    setPinInvalid(false);
    const result = await checkAuthStatus();
    if (result.pin === 'invalid' || result.pin === 'error') {
      setPinInvalid(true);
    }
    setValidating(false);
  };

  const handleForgetPin = () => {
    setBakerPin('');
    setPinValidated(false);
    setIncludePinInShare(false);
    setPinInvalid(false);
  };

  if (!open) {
    return null;
  }

  return (
    <Modal open={open} title={t('settings.title')} maxWidth="md" onClose={onClose}>
      <p className="mb-2 text-sm font-medium theme-muted">{t('settings.fontSize')}</p>
            <div className="mb-5 flex gap-2">
              <button
                type="button"
                onClick={() => setFontSize(100)}
                className={`flex-1 rounded-md border px-3 py-2 text-sm transition ${
                  fontSize === 100
                    ? 'border-emerald-500/60 bg-emerald-500/10 theme-accent'
                    : 'border-[color:var(--app-border)] hover:border-emerald-500/40'
                }`}
              >
                {t('settings.fontSizeNormal')}
              </button>
              <button
                type="button"
                onClick={() => setFontSize(125)}
                className={`flex-1 rounded-md border px-3 py-2 text-lg transition ${
                  fontSize === 125
                    ? 'border-emerald-500/60 bg-emerald-500/10 theme-accent'
                    : 'border-[color:var(--app-border)] hover:border-emerald-500/40'
                }`}
              >
                {t('settings.fontSizeLarge')}
              </button>
            </div>

            <p className="mb-2 text-sm font-medium theme-muted">{t('settings.share')}</p>
            <div className="mb-5 flex flex-col gap-3">
              <div className="flex-1 rounded-md border border-[color:var(--app-border)] px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-widest theme-muted">{t('share.url')}</p>
                <code className="mt-1 block break-all text-xs theme-accent">{shareUrl || t('share.loading')}</code>
              </div>
              <div className="flex flex-col items-center rounded-md border border-[color:var(--app-border)] px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-widest theme-muted">{t('share.qr')}</p>
                {qrSvg ? (
                  <div
                    role="img"
                    aria-label={t('share.qrAlt')}
                    className="mx-auto mt-2 h-56 w-56 rounded-md bg-white"
                    dangerouslySetInnerHTML={{ __html: qrSvg }}
                  />
                ) : (
                  <p className="mt-2 text-sm theme-muted">{t('share.generating')}</p>
                )}
              </div>
              {pinConfigured === true && bakerPin && (
                <div className="flex items-center justify-between gap-3 rounded-md border border-[color:var(--app-border)] px-3 py-2">
                  <span className="text-sm">{t('share.includePin')}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={includePinInShare}
                    aria-label={t('share.includePin')}
                    onClick={() => setIncludePinInShare(!includePinInShare)}
                    className={`relative h-5 w-9 shrink-0 rounded-full transition ${
                      includePinInShare ? 'bg-emerald-500' : 'bg-[color:var(--app-border)]'
                    }`}
                  >
                    <span
                      className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                        includePinInShare ? 'translate-x-4' : ''
                      }`}
                    />
                  </button>
                </div>
              )}
            </div>

            {pinConfigured === true && (
              <>
                <p className="mb-2 text-sm font-medium theme-muted">{t('settings.bakerPin')}</p>
                {pinValidated ? (
                  <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm theme-accent">
                    {t('settings.bakerPinValid')}
                    <button
                      type="button"
                      onClick={handleForgetPin}
                      className="ml-2 underline underline-offset-2 hover:opacity-80"
                    >
                      {t('settings.bakerPinForget')}
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="password"
                      value={bakerPin}
                      onChange={(event) => {
                        setBakerPin(event.target.value);
                        setPinInvalid(false);
                      }}
                      placeholder="••••"
                      className="w-full rounded-md border border-[color:var(--app-border)] bg-transparent px-3 py-2 text-sm transition focus:border-emerald-500/60 focus:outline-none"
                    />
                    {pinInvalid && (
                      <p className="mt-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs text-red-400">
                        {t('settings.bakerPinInvalid')}
                      </p>
                    )}
                    {bakerPin.length > 0 && !validating && (
                      <button
                        type="button"
                        onClick={handleValidatePin}
                        className="mt-1.5 theme-accent text-sm underline underline-offset-2 hover:opacity-80"
                      >
                        {t('settings.bakerPinValidate')}
                      </button>
                    )}
                    {validating && (
                      <p className="mt-1.5 text-xs theme-muted">{t('settings.bakerPinValidating')}</p>
                    )}
                  </>
                )}
                <p className="mt-1 mb-5 text-xs theme-muted">{t('settings.bakerPinHint')}</p>
              </>
            )}

            {sessionConfigured === true && (
              <>
                <p className="mb-2 text-sm font-medium theme-muted">{t('settings.session')}</p>
                {sessionValidated ? (
                  <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm theme-accent">
                    {t('settings.sessionJoined')}
                    <button
                      type="button"
                      onClick={() => {
                        setSessionId('');
                        setSessionValidated(false);
                      }}
                      className="ml-2 underline underline-offset-2 hover:opacity-80"
                    >
                      {t('settings.sessionForget')}
                    </button>
                  </div>
                ) : sessionId ? (
                  <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400">
                    {t('settings.sessionInvalid')}
                    <button
                      type="button"
                      onClick={() => {
                        setSessionId('');
                        setSessionValidated(false);
                      }}
                      className="ml-2 underline underline-offset-2 hover:opacity-80"
                    >
                      {t('settings.sessionForget')}
                    </button>
                  </div>
                ) : (
                  <p className="rounded-md border border-[color:var(--app-border)] px-3 py-2 text-sm theme-muted">
                    {t('settings.sessionJoinHint')}
                  </p>
                )}
                <p className="mt-1 mb-5 text-xs theme-muted">{t('settings.sessionHint')}</p>
              </>
            )}

            <p className="mb-2 text-sm font-medium theme-muted">{t('settings.language')}</p>
            {!languageOpen && (
              <button
                type="button"
                onClick={() => setLanguageOpen(true)}
                className="theme-accent text-sm underline underline-offset-2 hover:opacity-80"
              >
                {t('settings.changeLanguage', { lng: 'en' })} / {t('settings.changeLanguage')}
              </button>
            )}
            {languageOpen && (
              <>
                <div className="mt-2 space-y-1">
                  {SUPPORTED_LANGUAGES.map((entry) => {
                    const active = language === entry.code;
                    return (
                      <button
                        key={entry.code}
                        type="button"
                        onClick={() => setLanguage(entry.code)}
                        className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-sm text-left transition ${
                          active
                            ? 'border-emerald-500/60 bg-emerald-500/10 theme-accent'
                            : 'border-[color:var(--app-border)] hover:border-emerald-500/40'
                        }`}
                      >
                        <span className="text-base leading-none">{entry.flag}</span>
                        <span>{entry.name} / {entry.native}</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => setLanguageOpen(false)}
                  className="mt-3 theme-accent text-sm underline underline-offset-2 hover:opacity-80"
                >
                  {t('settings.hideLanguage', { lng: 'en' })} / {t('settings.hideLanguage')}
                </button>
              </>
            )}
    </Modal>
  );
}

export default SettingsOverlay;
